import { groupActingRepresentatives, pickT, representativeRoleLabel } from "@repo/shared";
import type { AssessmentAttribution } from "@repo/shared/types";
import { T } from "./group-answering-notes.i18n";

/**
 * QL-188 — the info note a group client sees on the components the group
 * answers together. Which wording applies is decided by the server's
 * `AssessmentAttribution` (QL-505): `representative` when a representative
 * acts for the group (the note names them, like the QL-505 notes), `members`
 * when the group has none.
 */
export type GroupAnsweringNotes = {
  sustainability: string;
  riskAssessment: string;
  goals: string;
  clientPreferences: string;
};

/**
 * The notes for a group client, or null when no note applies (not a group, or
 * the attribution has not loaded yet). A Person with a POA also gets a
 * `representative` attribution, so the client type gates it.
 */
export function groupAnsweringNotes(
  clientType: string | null | undefined,
  attribution: AssessmentAttribution | null | undefined,
  language: string,
): GroupAnsweringNotes | null {
  if (clientType !== "Group" || !attribution || attribution.kind === "none") return null;
  // A language the table doesn't have reads en_GB — the role label too.
  const lang = Object.hasOwn(T, language) ? language : "en_GB";
  const t = pickT(T, lang);
  // QL-654 — 2+ representatives answer as a list too (`members` with basis
  // `representatives`): they get the representative wording naming all of them;
  // only a group answered by its members gets the members wording.
  const rep = groupActingRepresentatives(attribution, language);
  if (!rep) return t.members;
  // QL-505 pattern: the note names the representative(s), like the K&E, quiz and
  // product assessment notes do.
  const memberRep = rep.memberRepresentative;
  const note = (key: keyof GroupAnsweringNotes) =>
    memberRep
      ? t.viaMember[key]({
          name: memberRep.name,
          role: representativeRoleLabel(memberRep.role, lang),
          member: memberRep.memberName,
        })
      : t.representative[key](rep.name || t.repGeneric, (rep.count ?? 0) > 1);
  return {
    sustainability: note("sustainability"),
    riskAssessment: note("riskAssessment"),
    goals: note("goals"),
    clientPreferences: note("clientPreferences"),
  };
}
